import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Form } from 'src/app/services/forms/form';
import { WfSignatureResource, WfSignature, WFConfiguration, ExtraAction, WFAction, SaveTaskResource, WFStepLocalResource, WFComponent } from 'src/app/components/workflow/workflow';
import { ComponentType, Fields, FormStatusEnum, FormType } from '../enumerations/enumerations';
import { BaseComponent } from '../base/base.component';
import { IFormService } from '../interfaces/i-form-service';
import { FormBaseV2 } from '../models/model-base';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { firstValueFrom } from 'rxjs';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';

@Component({
  selector: 'form-workflow',
  templateUrl: './form-workflow.component.html',
  styleUrls: ['./form-workflow.component.scss']
})
export class FormWorkflowComponent extends BaseComponent implements OnChanges {

  @Input() form?: Form | FormBaseV2 | null;
  @Input() sectionNumber!: number;
  @Input() amendmentSectionNumbers?: number[] = [];
  @Input() disabled?: boolean;
  @Input() overrideDisabledStep?: boolean;
  @Input() service?: IFormService;
  @Input() hideTitle?: boolean;
  @Input() privilegeWoApproval?: PrivilegeEnum;
  @Input() minorAmendmentIDs: number[] = [];

  @Output() signaturesCreated = new EventEmitter<WfSignature[]>();

  wfSignatures?: WfSignatureResource[];
  signatures!: WfSignature[];

  amendments!: Amendment[];

  configuration?: WFConfiguration[];

  type!: FormType;

  extraActions: ExtraAction[] = [];
  action = WFAction;

  sectionTitle?: string;
  isFullWidthSection?: boolean;
  formType?: string;
  serialNo?: string;

  constructor(
    protected override injector: Injector,
    private wf: WorkFlowService,
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService
  ) {
    super(injector);
  }

  /**
   * Non-production tester tool: whether the red "Tester Approve" button should be shown on this
   * signature. Gated defensively (mirrors the SCF form): only on non-production hosts, for System
   * Administrators, with the Tester Tools master switch ON, and only when the form's service
   * implements confirmTestSign (currently RSF only, so this shared component is inert for other
   * form types). It appears on the signature whose turn it is (backend testSignReady) while the
   * form is in a signable state, but NOT when the logged-in SA can genuinely sign it themselves
   * (a real, enabled Approve button is shown) — then they just use the normal button.
   */
  showTestApproveFor(s: WfSignature): boolean {
    return (
      !this.environmentService.isProduction() &&
      this.isSA() &&
      this.testerToolsService.enabled &&
      !!this.service?.confirmTestSign &&
      // Operational approvals are intentionally out of scope (as on the SCF form): they use a
      // separate two-phase, privilege-gated approve path (ApproveDisaproveOpApproval), not SignTask,
      // so the TestSign-based button must never surface on them.
      s.type !== ComponentType.OperationalApproval &&
      !!s.testSignReady &&
      !this.disabled &&
      !!s.approveActionId &&
      !(s.approveAction && !s.disabled)
    );
  }

  /**
   * Non-production tester tool: signs the given signature on behalf of the appropriate role-holder
   * via the service's confirmTestSign (server-side TestSign endpoint). The backend SignalR refresh
   * reloads the form; refresh locally only if the call was a no-op.
   */
  async testApprove(s: WfSignature) {
    const signed = await this.service?.confirmTestSign?.(this.form as Form, s, s.approveActionId, null);
    if (!signed) this.refresh();
  }


  /**
 * Synchronizes component state when form inputs change, extracting section metadata and serial numbers
 * based on form type, then triggers a signature refresh.
 * @param changes - Angular change detection object containing updated input properties
 */
  ngOnChanges(changes: SimpleChanges): void {
    if (this.form?.type)
      this.type = this.form.type;

    const section = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number == this.sectionNumber);
    if (this.form?.type != FormType.EBF && this.form?.type != FormType.PPSTB) {
      this.sectionTitle = section?.name;
      const form = this.form as FormBaseV2;
      this.serialNo = form?.serialNo;
    }
    else {
      const form = this.form as Form;
      this.serialNo = form?.sequenceNumber?.number;
      this.form.status = form?.formVersion?.statusID;
    }
    this.isFullWidthSection = section?.wfSignatures?.some(s => s.component?.fullWidth);
    this.formType = this.form?.type == null ? '' : FormType[this.form.type];

    this.refresh();
  }

  /**
   * Rebuilds the workflow signature list by merging main and amendment section signatures,
   * applying disabled states based on user permissions and step status, then emitting the result.
   * Updates `wfSignatures` and `signatures`, emits `signaturesCreated`, and triggers group creation.
   */
  refresh() {
    if (!this.form?.wfTable) return;

    // Create WFSignatures array from Form
    this.wfSignatures = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(x => x.number == this.sectionNumber)?.wfSignatures;
    this.amendmentSectionNumbers?.map(s => {
      var wfSignatures = this.form?.wfTable?.wfTableLocal?.wfSectionLocals.find(x => x.number == s)?.wfSignatures?.filter(x => x.amendmentNumber);
      if (wfSignatures)
        this.wfSignatures = this.wfSignatures?.concat(wfSignatures);
    });

    // Minor Amendments stop after the first configured approval, including completed amendment history.
    if (this.minorAmendmentIDs.length && this.wfSignatures) {
      this.minorAmendmentIDs.forEach(amendmentID => {
        const firstApproval = this.wfSignatures
          ?.filter(signature => signature.wfAmendmentID == amendmentID && !signature.requesterSignature && !!signature.approveID)
          .sort((a, b) =>
            (a.stepOrder ?? a.wfTaskLocal?.wfStepLocal?.order ?? 0) - (b.stepOrder ?? b.wfTaskLocal?.wfStepLocal?.order ?? 0) ||
            (a.order ?? 0) - (b.order ?? 0) ||
            a.id - b.id)
          .at(0);

        if (firstApproval)
          this.wfSignatures = this.wfSignatures?.filter(signature => signature.wfAmendmentID != amendmentID || signature.id == firstApproval.id);
      });
    }

    // If is Amendment then set Requesters
    if (this.wfSignatures)
      this.wfSignatures = this.service?.setAmendmentRequesters(this.wfSignatures);

    // Transform WFSignatures into Signatures
    this.signatures = [];
    this.wfSignatures?.map(signature => {
      const previousSigned = checkPreviousSigned(signature, this.wfSignatures, this.currentUser?.id);
      const stepDisabled = !this.overrideDisabledStep && !signature.wfTaskLocal.wfStepLocal?.wfStepStatusLocals.map(s => s.status).includes(this.form?.status);
      const signatureResource = WfSignatureResource.transformSignature(signature, previousSigned || stepDisabled || this.disabled, this.overrideDisabledStep, this.currentUser);
      signatureResource.status = this.form?.status;
      this.signatures.push(signatureResource);
    });

    this.signaturesCreated.emit(this.signatures);
    this.createGroups();

    function checkPreviousSigned(
      sig: WfSignatureResource,
      sigs: WfSignatureResource[] = [],
      userId?: number
    ): boolean {
      if (!userId) return false;

      const step = sig.wfTaskLocal.wfStepLocal;
      const stepId = sig.wfTaskLocal.wfStepLocalID;

      const sec = step?.wfSectionLocal;
      const secId = sec?.id;

      const noRepeatSec = sec?.code === 'NO_REPEAT_SIGNATURE';
      const noRepeatStp = !step?.samePersonApprove;

      const scopeSigs = noRepeatSec
        ? sigs.filter(s => s.wfTaskLocal.wfStepLocal?.wfSectionLocalID === secId)
        : noRepeatStp ? sigs.filter(s => s.wfTaskLocal.wfStepLocalID === stepId) : [];

      const hasPrev = scopeSigs.some(s =>
        s.amendmentNumber === sig.amendmentNumber &&
        s.signedBy?.id === userId &&
        s.id !== sig.id
      );

      return hasPrev;
    }
  }

  /**
 * Organizes signatures into a hierarchical structure of amendments and steps, sorted by amendment and step ID.
 * Updates `signatures` and `amendments` properties with the grouped structure.
 * @param signatures - Signature collection to organize; defaults to current signatures
 */
  createGroups(signatures: WfSignature[] = this.signatures) {
    // Create groups for Amendments and Steps
    this.signatures = signatures;
    this.amendments = [];
    this.signatures.sort((a, b) =>
      this.utils.sort((a.amendmentNumber ?? 0) * 1e10 + (a.stepId ?? 0), (b.amendmentNumber ?? 0) * 1e10 + (b.stepId ?? 0)))
      .forEach(s => {

        // Define Amendment
        let amendment;
        if (!this.amendments.map(a => a.number).includes(s.amendmentNumber ?? 0)) {
          // Use persisted amendment metadata so completed Minor Amendment groups retain their badge.
          amendment = {
            number: s.amendmentNumber ?? 0,
            isMinor: this.minorAmendmentIDs.includes(s.wfAmendmentID ?? 0),
            steps: []
          } as Amendment;
          this.amendments.push(amendment);
        }
        else amendment = this.amendments.find(a => a.number == (s.amendmentNumber ?? 0));

        // Define Step
        let step;
        if (!amendment?.steps?.map(step => step.id).includes(s.wfTaskLocal?.wfStepLocalID!)) {
          step = s.wfTaskLocal?.wfStepLocal;
          if (step) {
            step.signatures = [];
            step.active = step.wfStepStatusLocals.map(s => s.status).includes(this.form?.status) && step.wfSteps?.find(x => x.
              wfAmmendmentID == s.wfAmendmentID)?.active;
            amendment?.steps?.push(step);
          }
        }
        else step = amendment?.steps.find(x => x.id == s.wfTaskLocal?.wfStepLocalID);

        // Add Signature to Step
        step?.signatures?.push(s);
        if (step)
          step.visible = step.signatures?.some(s => s.visible) ?? false;
      });

    // Hide amendment headers when every workflow control in the group is invisible.
    this.amendments = this.amendments.filter(amendment =>
      amendment.number === 0 || (amendment.steps?.some(step => step.signatures?.some(signature => signature.visible)) ?? false));
  }

  isAllowedToSign(step: WFStepLocalResource) {
    if (!this.form) return false;
    return;
  }

  async sign(s: WfSignature, action: WFAction, data: any = null) {
    let options = undefined;
    if (data?.options) {
      options = this.utils.JSONstringify(data.options);
    }
    const actionID = action == WFAction.Approve ? s.approveActionId : action == WFAction.Unapprove ? s.unapproveActionId : action == WFAction.Disapprove ? s.disapproveActionId : 0;
    const signed = await this.service?.confirmSign(this.form as Form, s, actionID, data, options);
    if (!signed) this.refresh();
  }

  check(e: boolean, s: WfSignature) {
    this.sign(s, e ? WFAction.Approve : WFAction.Unapprove);
  }


  /**
 * Handles signature text changes or approval actions, saving the form if dirty, then persisting the task update.
 * Determines whether the change requires pending approval based on form status and user privileges.
 * @param e - New text value entered by the user
 * @param s - Signature being modified
 * @param action - Approval action (Approve/Disapprove); omitted for text changes
 */
  changed(text: string, s: WfSignature, action: WFAction = WFAction.Update) {
    // 1) Save the form if it's dirty
    if (this.isFormDirty() && (this.form?.status == FormStatusEnum.New || this.form?.status == FormStatusEnum.Draft) && this.service) {
      const form = this.form as FormBaseV2;
      this.service.save(form);
      this.setFormDirty(false);
    }

    switch (action) {
      case WFAction.Update:
        this.wf
          .opApprovalTaskChanged(
            this.form!.type!,
            text,
            s.id!,
            this.form!.status!,
            this.form?.type == FormType.RSF ? PrivilegeEnum.RSFOpAppWoApproval : PrivilegeEnum.None
          );
        break;

      case WFAction.Approve:
        firstValueFrom(this.wf
          .approveOpApproval(s.id!, true));
        break;

      case WFAction.Disapprove:
        firstValueFrom(this.wf
          .approveOpApproval(s.id!, false));
        break;
    }
  }

  close(s: WfSignature) {
    this.sign(s, WFAction.Approve);
  }

  hasVisibleSignatures(step: WFStepLocalResource): boolean {
    return step.signatures?.some(s => s.visible) ?? false;
  }
}

interface Amendment {
  number: number;
  isMinor?: boolean;
  steps?: WFStepLocalResource[];
}
